import { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import TaskForm from '../components/tasks/TaskForm';
import TaskList from '../components/tasks/TaskList';
import { FaPlus, FaFilter, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import useAuth from '../hooks/useAuth';

const TaskManagement = () => {
    const { user, updateUser } = useAuth();
    const [newCategory, setNewCategory] = useState('');
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editedCategoryName, setEditedCategoryName] = useState('');

    // State for tasks and UI
    const [tasks, setTasks] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [taskToEdit, setTaskToEdit] = useState(null);
    const [filter, setFilter] = useState('All');

    // Color Palette for Custom Categories
    const CUSTOM_COLORS = ['#6c5ce7', '#e84393', '#00b894', '#fdcb6e'];

    // Construct full category list with colors
    const defaultCategories = [
        { id: 'Adoration', label: 'Adoration', color: '#1B7A6E', bg: '#E8F5E9' },
        { id: 'Travail', label: 'Travail', color: '#0984e3', bg: '#E3F2FD' },
        { id: 'Famille', label: 'Famille', color: '#e17055', bg: '#FBE9E7' }
    ];

    const customCategoriesList = (user?.customCategories || []).map((cat, index) => ({
        id: cat,
        label: cat,
        color: CUSTOM_COLORS[index % CUSTOM_COLORS.length],
        bg: `${CUSTOM_COLORS[index % CUSTOM_COLORS.length]}20` // 20% opacity hex
    }));

    const allCategories = [...defaultCategories, ...customCategoriesList];

    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (!newCategory.trim()) return;

        if ((user?.customCategories?.length || 0) >= 4) {
            alert("Vous avez atteint la limite de 4 catégories personnalisées.");
            return;
        }

        try {
            const res = await apiClient.put('/users/categories', { category: newCategory.trim() });

            // Update user in context
            const updatedUser = { ...user, customCategories: res.data };
            updateUser(updatedUser);

            // Reset UI
            setNewCategory('');
            setIsAddingCategory(false);
        } catch (error) {
            console.error('Failed to add category', error);
        }
    };

    const handleEditClick = (category) => {
        setEditingCategory(category);
        setEditedCategoryName(category);
    };

    const handleSaveEdit = async (oldCategory) => {
        if (!editedCategoryName.trim() || editedCategoryName === oldCategory) {
            setEditingCategory(null);
            return;
        }

        try {
            const res = await apiClient.put(`/users/categories/${oldCategory}`, { newCategory: editedCategoryName.trim() });
            const updatedUser = { ...user, customCategories: res.data };
            updateUser(updatedUser);

            // Update local tasks to reflect change immediately
            setTasks(tasks.map(t =>
                t.category === oldCategory ? { ...t, category: editedCategoryName.trim() } : t
            ));

            setEditingCategory(null);
        } catch (error) {
            console.error('Failed to update category', error);
            const msg = error.response?.data?.message || "Impossible de modifier la catégorie.";
            alert(msg);
        }
    };

    const handleDeleteCategory = async (category) => {
        if (window.confirm(`Voulez-vous vraiment supprimer la catégorie "${category}" ?`)) {
            try {
                const res = await apiClient.delete(`/users/categories/${category}`);
                const updatedUser = { ...user, customCategories: res.data };
                updateUser(updatedUser);
            } catch (error) {
                console.error('Failed to delete category', error);
                const msg = error.response?.data?.message || "Impossible de supprimer la catégorie.";
                alert(msg);
            }
        }
    };

    const fetchTasks = async () => {
        const res = await apiClient.get('/tasks');
        return Array.isArray(res.data) ? res.data : [];
    };

    const addTask = async (task) => {
        const res = await apiClient.post('/tasks', task);
        setTasks([...tasks, res.data]);
        setShowModal(false);
    };

    const updateTask = async (id, updatedTask) => {
        const res = await apiClient.put(`/tasks/${id}`, updatedTask);
        setTasks(tasks.map((task) => (task._id === id ? res.data : task)));
        setTaskToEdit(null);
        setShowModal(false);
    };

    const deleteTask = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette tâche ?')) {
            await apiClient.delete(`/tasks/${id}`);
            setTasks(tasks.filter((task) => task._id !== id));
        }
    };

    const toggleCompletion = async (task) => {
        const updTask = { ...task, completed: !task.completed };
        const res = await apiClient.put(`/tasks/${task._id}`, updTask);

        setTasks(
            tasks.map((t) =>
                t._id === task._id ? { ...t, completed: res.data.completed } : t
            )
        );
    };

    const editTask = (task) => {
        setTaskToEdit(task);
        setShowModal(true);
    };

    const openModal = () => {
        setTaskToEdit(null);
        setShowModal(true);
    };

    const closeModal = () => {
        setTaskToEdit(null);
        setShowModal(false);
    };

    const filteredTasks = filter === 'All'
        ? tasks
        : tasks.filter((task) => task.category === filter);

    useEffect(() => {
        const getTasks = async () => {
            const tasksFromServer = await fetchTasks();
            setTasks(tasksFromServer);
        };

        getTasks();
    }, []);

    return (
        <div className="task-management-container">
            <div className="header-tasks">
                <div>
                    <h1>Gestion des Tâches</h1>
                    <p style={{ color: '#636E72' }}>Organisez votre journée et vos objectifs</p>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={openModal}
                >
                    <FaPlus /> Ajouter une tâche
                </button>
            </div>

            {/* Custom Category Management Section */}
            <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: '600', color: '#2D3436' }}>Mes Catégories :</span>
                {allCategories.map((cat) => {
                    const isCustom = !defaultCategories.find(c => c.id === cat.id);
                    const isEditing = editingCategory === cat.id;

                    return (
                        <span
                            key={cat.id}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '20px',
                                backgroundColor: cat.bg,
                                color: cat.color,
                                fontSize: '0.85rem',
                                fontWeight: '500',
                                border: `1px solid ${cat.color}40`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            {isEditing ? (
                                <>
                                    <input
                                        value={editedCategoryName}
                                        onChange={(e) => setEditedCategoryName(e.target.value)}
                                        autoFocus
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            outline: 'none',
                                            color: cat.color,
                                            fontWeight: '500',
                                            width: '80px'
                                        }}
                                    />
                                    <FaCheck
                                        size={12}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleSaveEdit(cat.id)}
                                    />
                                    <FaTimes
                                        size={12}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setEditingCategory(null)}
                                    />
                                </>
                            ) : (
                                <>
                                    {cat.label}
                                    {isCustom && (
                                        <div style={{ display: 'flex', gap: '5px', marginLeft: '5px' }}>
                                            <FaEdit
                                                size={10}
                                                style={{ cursor: 'pointer', opacity: 0.7 }}
                                                onClick={() => handleEditClick(cat.id)}
                                                title="Modifier"
                                            />
                                            <FaTrash
                                                size={10}
                                                style={{ cursor: 'pointer', opacity: 0.7 }}
                                                onClick={() => handleDeleteCategory(cat.id)}
                                                title="Supprimer"
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </span>
                    );
                })}

                {(user?.customCategories?.length || 0) < 4 && (
                    !isAddingCategory ? (
                        <button
                            onClick={() => setIsAddingCategory(true)}
                            style={{
                                background: 'transparent',
                                border: '1px dashed #B2BEC3',
                                borderRadius: '20px',
                                padding: '6px 12px',
                                color: '#636E72',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                fontSize: '0.85rem'
                            }}
                        >
                            <FaPlus size={10} /> Créer
                        </button>
                    ) : (
                        <form onSubmit={handleAddCategory} style={{ display: 'flex', gap: '5px' }}>
                            <input
                                type="text"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                                placeholder="Nom..."
                                autoFocus
                                style={{
                                    padding: '6px 12px',
                                    borderRadius: '20px',
                                    border: '1px solid #00B894',
                                    outline: 'none',
                                    fontSize: '0.85rem',
                                    width: '120px'
                                }}
                            />
                            <button
                                type="submit"
                                style={{
                                    background: '#00B894',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '28px',
                                    height: '28px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <FaPlus size={10} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsAddingCategory(false)}
                                style={{
                                    background: '#FF7675',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '28px',
                                    height: '28px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <FaPlus size={10} style={{ transform: 'rotate(45deg)' }} />
                            </button>
                        </form>
                    )
                )}
            </div>

            <div className="filter-container">
                <FaFilter style={{ color: '#1B7A6E' }} />
                <span style={{ fontWeight: '600' }}>Filtrer par : </span>
                <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                    <option value="All">Toutes les catégories</option>
                    {allCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                </select>
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>{taskToEdit ? 'Modifier la tâche' : 'Nouvelle tâche'}</h2>
                            <button className="btn-close" onClick={closeModal}>&times;</button>
                        </div>
                        <TaskForm
                            onAdd={addTask}
                            onUpdate={updateTask}
                            taskToEdit={taskToEdit}
                            onCancel={closeModal}
                            categories={allCategories}
                        />
                    </div>
                </div>
            )}

            <TaskList
                tasks={filteredTasks}
                onDelete={deleteTask}
                onToggle={toggleCompletion}
                onEdit={editTask}
            />
        </div>
    );
};

export default TaskManagement;
